import React from 'react';
import {   InfoBox,   ExerciseBox,   CodeBlock,   InlineCode,  Result, SectionTitle, Image, Separator,CorrectionBox,  Note,  TableContainer,  Highlight,  PatternContainer,  SectionHeading,  SubHeading,  TwoColGrid,  ThreeColGrid,  GridItem,  Divider} from '../../../../components/course/CourseComponents';
import screen5 from '../screen5.jpg';

const Chapitre5Content = () => {
  return (
    <>
    <InfoBox title="Objectif du chapitre" color="indigo">
      <p className="mb-0">
        Détection du gagnant ou de l'égalité et écran de fin de jeu.
      </p>
    </InfoBox>      

    <Separator type="text" text="Détection du vainqueur" color="gray-darker" size="md" />
    <SectionTitle>🏆 1/ Détection du vainqueur</SectionTitle>

    <p>Pour savoir si un joueur a gagné, on crée une fonction <InlineCode>verifierGagnant</InlineCode>, qui vérifie chaque possibilité de victoire, et qui renvoie <InlineCode>true</InlineCode>(vrai) s'il y a un gagnant, ou <InlineCode>false</InlineCode>(faux) s'il n'y en a pas.</p>

    <p>Pour qu'il y ait un gagnant, il faut qu'il y ait le même symbole sur :</p>
    <ul className="list-disc list-inside mb-4">
      <li>Une même ligne</li>
      <li>Une même colonne</li>
      <li>Une diagonale</li>
    </ul>

    <p>Pour cela, on utilise notre tableau <InlineCode>grille</InlineCode> qui contient tous les coups joués, et on teste toutes les possibilités de gain de manière exhaustive.</p>

    <Image src={screen5} alt="Grille du Morpion" width="370px" height="370px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

    <p><strong>Exemple pour la ligne 0 :</strong></p>
    <p>Si <InlineCode>grille[0][0] == grille[0][1] == grille[0][2]</InlineCode>, alors il y a un gagnant !</p>

    <Note type="warning" title="⚠️ Attention">
      Il faut vérifier que le symbole sur cette ligne n'est pas vide, sinon on détecterait un gagnant dès le début de la partie, car toutes les cases sont vides au départ.
    </Note>

    <p>Donc pour la ligne 0, il faut que chaque case soit identique, et différente de <InlineCode>""</InlineCode> :</p>
    <CodeBlock language="javascript">
{`if ( grille[0][0] != "" && grille[0][0] == grille[0][1] && grille[0][1] == grille[0][2]) {
  // Il y a un gagnant !
}`}
    </CodeBlock>

    <p>On fait pareil pour toutes les possibilités :</p>
    <ul className="list-disc list-inside mb-4">
      <li><strong>Lignes :</strong> ligne 0, ligne 1, ligne 2</li>
      <li><strong>Colonnes :</strong> colonne 0, colonne 1, colonne 2</li>
      <li><strong>Diagonales :</strong> diagonale principale et diagonale secondaire</li>
    </ul>

    <Note type="exercice" title="🧪 À toi de jouer : création de la fonction verifierGagnant">
      En bas de ton code, crée une nouvelle fonction <InlineCode>verifierGagnant()</InlineCode> qui renvoie <InlineCode>true</InlineCode> s'il y a un gagnant, <InlineCode>false</InlineCode> sinon.<br/>
      <br/>
      Teste toutes les possibilités :<br/>
      &nbsp;&nbsp;- Les 3 lignes<br/>
      &nbsp;&nbsp;- Les 3 colonnes<br/>
      &nbsp;&nbsp;- Les 2 diagonales<br/>
      <br/>
      <Result>S'il y a le même symbole sur une ligne, une colonne ou une diagonale, la fonction renvoie true, sinon false.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`function verifierGagnant() {
  // Lignes
  if (grille[0][0] != "" && grille[0][0] == grille[0][1] && grille[0][1] == grille[0][2]) return true;
  if (grille[1][0] != "" && grille[1][0] == grille[1][1] && grille[1][1] == grille[1][2]) return true;
  if (grille[2][0] != "" && grille[2][0] == grille[2][1] && grille[2][1] == grille[2][2]) return true;

  // Colonnes
  if (grille[0][0] != "" && grille[0][0] == grille[1][0] && grille[1][0] == grille[2][0]) return true;
  if (grille[0][1] != "" && grille[0][1] == grille[1][1] && grille[1][1] == grille[2][1]) return true;
  if (grille[0][2] != "" && grille[0][2] == grille[1][2] && grille[1][2] == grille[2][2]) return true;

  // Diagonales
  if (grille[0][0] != "" && grille[0][0] == grille[1][1] && grille[1][1] == grille[2][2]) return true;
  if (grille[0][2] != "" && grille[0][2] == grille[1][1] && grille[1][1] == grille[2][0]) return true;

  return false;
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <p>On va appeler cette fonction à chaque fois qu'un joueur clique sur une case :</p>
    <p>Le joueur clique sur une case → on met à jour la grille → on vérifie s'il y a un gagnant. S'il y en a un, on stocke le gagnant dans la variable <InlineCode>gagnant</InlineCode>, sinon on change de joueur et on continue.</p>

    <Note type="exercice" title="🧪 À toi de jouer : utilisation de la fonction verifierGagnant">
      Crée une variable globale <InlineCode>gagnant = ""</InlineCode>.<br/>
      <br/>
      Dans la fonction <InlineCode>mousePressed</InlineCode> :<br/>
      &nbsp;&nbsp;- Juste avant de changer de joueur, appelle <InlineCode>verifierGagnant</InlineCode><br/>
      &nbsp;&nbsp;- Si elle renvoie <InlineCode>true</InlineCode>, alors le joueur qui vient de jouer à gagné : <InlineCode>gagnant = joueur_courant</InlineCode><br/>
      &nbsp;&nbsp;- Sinon on change de joueur<br/>
      <br/>
      Teste ton code en affichant dans <InlineCode>draw</InlineCode>, la variable <InlineCode>gagnant</InlineCode> si elle est différente de <InlineCode>""</InlineCode> :<br/>
      &nbsp;&nbsp;- <InlineCode>if (gagnant != "") print(gagnant);</InlineCode><br/>
      <br/>
      <Result>Quand un joueur gagne, son symbole ("X" ou "O") s'affiche dans la console.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`// Changement de joueur ou gagnant
if (verfierGagnant())  gagnant = joueur_courant;
else if (joueur_courant == "X") joueur_courant = "O";
else joueur_courant = "X";
`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <Separator type="text" text="Détection d'une égalité" color="gray-darker" size="md" />
    <SectionTitle>🤝 2/ Détection d'une égalité</SectionTitle>

    <p>Comme pour le vainqueur, on crée une fonction <InlineCode>verifierEgalite</InlineCode> qui renvoie <InlineCode>true</InlineCode> s'il y a égalité, <InlineCode>false</InlineCode> sinon.</p>

    <p>Pour savoir s’il y a une égalité, c’est simple : s’il n’y a pas de gagnant et que toutes les cases de notre tableau sont différentes de vide, alors il y a égalité.</p>

    <Note type="exercice" title="🧪 À toi de jouer : création de la fonction verifierEgalite">
      En bas de ton code, crée <InlineCode>verifierEgalite()</InlineCode> :<br/>
      &nbsp;&nbsp;- Parcours la grille avec deux boucles for<br/>
      &nbsp;&nbsp;- Si tu trouves une case vide, retourne <InlineCode>false</InlineCode><br/>
      &nbsp;&nbsp;- Si après avoir parcouru tout ton tableau, tu n’as pas renvoyé <InlineCode>false</InlineCode>, alors il y a égalité : renvoie <InlineCode>true</InlineCode>.
      <br/><br/>
      <Result>La fonction détecte correctement quand toutes les cases sont remplies.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`function verifierEgalite() {
  // Vérifie s'il reste une case vide
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      if (grille[i][j] == "") return false; // pas encore fini
    }
  }
  return true; // aucune case vide : égalité
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <p>Maintenant, on appelle cette fonction lorsqu’un joueur vient de jouer, juste après avoir vérifié s’il y avait un gagnant :</p>
<p>Le joueur clique sur une case → on met à jour notre grille → on vérifie s’il y a un gagnant → s’il n’y a pas de gagnant, on vérifie s’il y a égalité.
S’il y a égalité, alors on met un "E" (comme Égalité) dans la variable gagnant. Sinon, on change de joueur.</p>

    <p>La variable <InlineCode>gagnant</InlineCode> pourra donc avoir 4 valeurs :</p>
    <ul className="list-disc list-inside mb-4">
      <li><InlineCode>""</InlineCode> → la partie est en cours</li>
      <li><InlineCode>"E"</InlineCode> → égalité</li>
      <li><InlineCode>"X"</InlineCode> → le joueur X a gagné</li>
      <li><InlineCode>"O"</InlineCode> → le joueur O a gagné</li>
    </ul>

    <CodeBlock language="javascript">
{`// Changement de joueur ou gagnant
if (verfierGagnant())  gagnant = joueur_courant;
else if(verifierEgalite()) gagnant = "E";
else if (joueur_courant == "X") joueur_courant = "O";
else joueur_courant = "X";`}
    </CodeBlock>

    <Separator type="text" text="Affichage de l'écran de fin" color="gray-darker" size="md" />
    <SectionTitle>🎉 3/ Affichage de l'écran de fin</SectionTitle>

    <p>  Si la variable <InlineCode>gagnant</InlineCode> est différente de <InlineCode>""</InlineCode>, la partie est terminée.</p>
<p>On affiche alors un écran de fin avec le nom du vainqueur ou un message d’égalité.</p>

    <p>Pour ce faire, à la fin de <InlineCode>draw</InlineCode>, on efface l’écran en rappelant la fonction <InlineCode>background</InlineCode>, et on utilise la fonction <InlineCode>text</InlineCode> pour afficher le vainqueur.</p>

    <Note type="exercice" title="🧪 À toi de jouer : écran de fin">
      À la fin de <InlineCode>draw</InlineCode>, si <InlineCode>gagnant != ""</InlineCode> :<br/>
      &nbsp;&nbsp;- Efface l'écran : <InlineCode>background(220, 220, 220)</InlineCode><br/>
      &nbsp;&nbsp;- Centre le texte : <InlineCode>textAlign(CENTER, CENTER)</InlineCode><br/>
      &nbsp;&nbsp;- Taille : <InlineCode>textSize(32)</InlineCode><br/>
      &nbsp;&nbsp;- Couleur : <InlineCode>fill(0, 0, 0)</InlineCode><br/>
      &nbsp;&nbsp;- Si <InlineCode>gagnant == "E"</InlineCode> → afficher "🤝 Égalité !"<br/>
      &nbsp;&nbsp;- Sinon → "🎉 Le gagnant est : " + <InlineCode>gagnant</InlineCode><br/>
      <br/>
      <Result>Un écran de fin s'affiche quand la partie est terminée.</Result>

      <CorrectionBox>
        <CodeBlock language="javascript">
{`if (gagnant != "") {
  background(220, 220, 220);
  fill(0, 0, 0);
  textAlign(CENTER, CENTER);
  textSize(32);
  if (gagnant == "E") {
    text("🤝 Égalité !", width / 2, height / 2);
  } else {
    text("🎉 Le gagnant est : " + gagnant + " 🎉", width / 2, height / 2);
  }
}`}
        </CodeBlock>
      </CorrectionBox>
    </Note>

    <p>Et enfin, si la partie est finie, on ne doit plus rien faire dans <InlineCode>mousePressed</InlineCode> :</p>
    <CodeBlock language="javascript">
{`function mousePressed() {
  if (gagnant != "") return; // Ne rien faire si la partie est finie
  // ... reste du code
}`}
    </CodeBlock>

    <Separator type="text" text="Code complet" color="gray-darker" size="md" />
    <SectionTitle>✅ Code complet du jeu : Le Morpion</SectionTitle>

    <p>Voici le code complet du jeu :</p>

          <CodeBlock language="javascript">
{`// Jeu 2 : le Morpion

let largeur_case;
let hauteur_case;
let grille;
let joueur_courant = "X";
let gagnant = "";

function setup() {
  createCanvas(600, 600);
  largeur_case = width / 3;
  hauteur_case = height / 3;
 
  grille = [
    ["", "", ""],
    ["", "", ""],
    ["", "", ""]
  ];
}

function draw() {
  background(220, 220, 220);

  // Plateau de jeu
  strokeWeight(4);
  for (let i = 1; i < 3; i++) {
    line(i * largeur_case, 0, i * largeur_case, height);
    line(0, i * hauteur_case, width, i * hauteur_case);
  }
 
  // Afficher les X et O avec couleurs
  textAlign(CENTER, CENTER);
  textSize(150);
  textStyle(NORMAL);
  for (let ligne = 0; ligne < 3; ligne++) {
    for (let colonne = 0; colonne < 3; colonne++) {
      let centre_case_x = colonne * largeur_case + largeur_case / 2;
      let centre_case_y = ligne * hauteur_case + hauteur_case / 2;
      if (grille[ligne][colonne] == "X") {
        fill(255, 0, 0); // Rouge
        text("X", centre_case_x, centre_case_y);
      } else if (grille[ligne][colonne] == "O") {
        fill(0, 0, 255); // Bleu
        text("O", centre_case_x, centre_case_y);
      }
    }
  }

  // Afficher le joueur courant avec couleur
  textAlign(LEFT);
  textSize(16);
  textStyle(BOLD);
  if (joueur_courant == "X") {
    fill(255, 0, 0); // Rouge
    text("Joueur X", 2, 10);
  } else {
    fill(0, 0, 255); // Bleu
    text("Joueur O", 2, 10);
  }

  // Écran de fin
  if (gagnant != "") {
    background(220, 220, 220);
    fill(0, 0, 0);
    textAlign(CENTER, CENTER);
    textSize(32);
    if (gagnant == "E") {
      text("🤝 Égalité !", width / 2, height / 2);
    } else {
      text("🎉 Le gagnant est : " + gagnant + " 🎉", width / 2, height / 2);
    }
  }
}

function mousePressed() {
  if (gagnant != "") return; // Ne rien faire si la partie est finie

  let case_x = floor(mouseY / hauteur_case);
  let case_y = floor(mouseX / largeur_case);

  if (case_x < 0 || case_x > 2 || case_y < 0 || case_y > 2)  
    return; // Clique en dehors du plateau
 
  if (grille[case_x][case_y] == "") {
    grille[case_x][case_y] = joueur_courant;
   
    // Vérification du gagnant ou égalité
    if (verifierGagnant()) {
      gagnant = joueur_courant;
    } else if (verifierEgalite()) {
      gagnant = "E";
    } else {
      // Changement de joueur
      if (joueur_courant == "X") joueur_courant = "O";
      else joueur_courant = "X";
    }
  }
}

function verifierGagnant() {
  // Lignes
  if (grille[0][0] != "" && grille[0][0] == grille[0][1] && grille[0][1] == grille[0][2]) return true;
  if (grille[1][0] != "" && grille[1][0] == grille[1][1] && grille[1][1] == grille[1][2]) return true;
  if (grille[2][0] != "" && grille[2][0] == grille[2][1] && grille[2][1] == grille[2][2]) return true;

  // Colonnes
  if (grille[0][0] != "" && grille[0][0] == grille[1][0] && grille[1][0] == grille[2][0]) return true;
  if (grille[0][1] != "" && grille[0][1] == grille[1][1] && grille[1][1] == grille[2][1]) return true;
  if (grille[0][2] != "" && grille[0][2] == grille[1][2] && grille[1][2] == grille[2][2]) return true;

  // Diagonales
  if (grille[0][0] != "" && grille[0][0] == grille[1][1] && grille[1][1] == grille[2][2]) return true;
  if (grille[0][2] != "" && grille[0][2] == grille[1][1] && grille[1][1] == grille[2][0]) return true;

  return false;
}

function verifierEgalite() {
  // Vérifie s'il reste une case vide
  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      if (grille[i][j] == "") return false; // pas encore fini
    }
  }
  // Pas de gagnant, et aucune case vide : égalité
  return true;
}`}
    </CodeBlock>

    <Note type="info" title="🎉 Félicitations !">
    Bravo ! Ton jeu de morpion est maintenant terminé et fonctionnel 🎉 Les joueurs peuvent s'affronter, le jeu détecte automatiquement le gagnant ou une égalité, et affiche un écran de fin.<br/><br/>

    J’espère que tu as appris plein de choses !<br/><br/>
    N’hésite pas à revenir dans les chapitres précédents si tu as encore des points à approfondir, ou à venir poser tes questions sur Discord 🙂
    </Note>
    </>
  );
};

export default Chapitre5Content; 